import {Edit} from "@refinedev/mui";
import {Box, Checkbox, FormControlLabel, TextField, Alert, MenuItem, Autocomplete, CircularProgress, Chip, Stack, Typography} from "@mui/material";
import {useForm} from "@refinedev/react-hook-form";
import {IResourceComponentsProps, useTranslate, useNotification} from "@refinedev/core";
import {Controller} from "react-hook-form";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/auth-context";
import { canAccessUserData } from "../../utils/role-permissions";
import { PhoneNumberInput } from "../../components/common/PhoneInput";
import axiosInstance from "../../api/axios-config";

const OPERATOR_ROLES = ['OPERATOR', 'VIGILANE', 'OT'];

interface SubGroup {
    id: number;
    name: string;
    parentId?: number;
    depth?: number;
}

export const UserEdit: React.FC<IResourceComponentsProps> = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user: currentUser } = useAuth();
    const translate = useTranslate();
    const { open: notify } = useNotification();

    // State for sub-groups
    const [subGroups, setSubGroups] = useState<SubGroup[]>([]);
    const [loadingSubGroups, setLoadingSubGroups] = useState(false);

    // State for externalId (lives in UserMonitoring, not User)
    const [externalId, setExternalId] = useState<string>("");
    const isOperatorRole = OPERATOR_ROLES.includes(currentUser?.role ?? '');

    const {
        saveButtonProps,
        register,
        control,
        watch,
        reset,
        formState: { errors },
        refineCore: { onFinish, queryResult },
    } = useForm({
        refineCoreProps: {
            onMutationSuccess: () => {
                // User update successful
            },
            onMutationError: (error) => {
                console.error('Error during user update:', error);
            }
        }
    });

    const record = queryResult?.data?.data;
    const isLoading = queryResult?.isLoading ?? true;
    const error = queryResult?.error;

    // Show externalId for OPERATOR_ROLES (always) or ADMIN/SAFEE_MODERATOR (if user has monitoring)
    const canEditExternalId = isOperatorRole
        || (['ADMIN', 'SAFEE_MODERATOR'].includes(currentUser?.role ?? '') && !!record?.userMonitoring);

    // Update form values when record is loaded
    useEffect(() => {
        if (record) {
            const formData = {
                ...record,
                role: record.role || "USER", // Ensure role has a value
                password: "", // Clear password field
                confirmPassword: ""
            };

            // Reset form with the loaded data
            reset(formData);

            // Pre-fill externalId from userMonitoring (separate table)
            if (record.userMonitoring?.externalId) {
                setExternalId(record.userMonitoring.externalId);
            }
        }
    }, [record, reset]);

    // Fetch sub-groups for ADMIN and MODERATOR users
    useEffect(() => {
        const fetchSubGroups = async () => {
            // Only fetch if user can manage sub-groups
            const canManageSubGroups = ['ADMIN', 'MODERATOR', 'MODERATOR_FREE', 'SAFEE_MODERATOR'].includes(currentUser?.role || '');
            if (!canManageSubGroups) return;

            setLoadingSubGroups(true);
            try {
                const response = await axiosInstance.get('/sub-groups');
                if (Array.isArray(response.data)) {
                    // Calculate depth for display indentation
                    const calculateDepth = (groups: SubGroup[], parentId: number | null = null, depth = 0): SubGroup[] => {
                        return groups
                            .filter(g => g.parentId === parentId)
                            .flatMap(g => [
                                { ...g, depth },
                                ...calculateDepth(groups, g.id, depth + 1)
                            ]);
                    };
                    const sortedGroups = calculateDepth(response.data);
                    setSubGroups(sortedGroups);
                }
            } catch (error) {
                console.error('Failed to fetch sub-groups:', error);
            } finally {
                setLoadingSubGroups(false);
            }
        };

        fetchSubGroups();
    }, [currentUser?.role]);

    // Custom transform function to process form data before submission
    const transformFormData = (data: any) => {
        // Remove confirmPassword from data since it's only used for validation
        const { confirmPassword, ...submitData } = data;

        // Only include password if it's provided and not empty
        if (!submitData.password || submitData.password.trim() === '') {
            delete submitData.password;
        }

        return submitData;
    };

    // Company-based access control
    useEffect(() => {
        if (!isLoading && record && currentUser) {
            // Check if user can access the target user data
            if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                console.warn(`🚫 Access denied: User ${currentUser.id} tried to edit user ${record.id} from different company`);
                navigate('/users', {
                    replace: true
                });
                return;
            }
        }
    }, [record, currentUser, isLoading, navigate]);

    // Don't render if access should be denied
    if (!isLoading && record && currentUser &&
        !canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
        return null; // Component will unmount due to navigation
    }

    // Show error if user data failed to load
    if (error) {
        return (
            <Alert severity="error">
                Failed to load user information for editing
            </Alert>
        );
    }

    return (
        <Edit
            saveButtonProps={{
                ...saveButtonProps,
                onClick: async (e) => {
                    // Save externalId via dedicated monitoring endpoint (any role that can edit it)
                    if (canEditExternalId && id) {
                        const externalIdChanged = externalId !== (record?.userMonitoring?.externalId ?? "");
                        if (externalIdChanged) {
                            try {
                                await axiosInstance.patch(
                                    `/monitoring/operators/managed-users/${id}/external-id`,
                                    { externalId: externalId.trim() || null },
                                );
                                notify?.({
                                    type: "success",
                                    message: "External ID updated",
                                });
                            } catch (err: any) {
                                notify?.({
                                    type: "error",
                                    message: "Failed to update External ID",
                                    description: err?.response?.data?.message || err?.message,
                                });
                                return;
                            }
                        }
                    }

                    // OPERATOR only edits externalId — skip standard user update
                    if (isOperatorRole) {
                        navigate(`/users/${id}`);
                        return;
                    }

                    const formData = watch();
                    const processedData = transformFormData(formData);

                    // Manually trigger the standard user update
                    onFinish(processedData);
                }
            }}
        >
            <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
            >
                <TextField
                    {...register("id", {
                        required: "This field is required",
                        valueAsNumber: true,
                    })}
                    error={!!(errors as any)?.id}
                    helperText={(errors as any)?.id?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="number"
                    label="Id"
                    name="id"
                    disabled
                />
                <TextField
                    {...register("email", {
                        required: "This field is required",
                    })}
                    error={!!(errors as any)?.email}
                    helperText={(errors as any)?.email?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="email"
                    label="Email"
                    name="email"
                    disabled={isOperatorRole}
                />
                <TextField
                    {...register("fullname", {
                        required: "This field is required",
                    })}
                    error={!!(errors as any)?.fullname}
                    helperText={(errors as any)?.fullname?.message}
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    type="text"
                    label="Fullname"
                    name="fullname"
                    disabled={isOperatorRole}
                />
                <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState }) => (
                        <PhoneNumberInput
                            {...field}
                            label={translate("users.fields.phone")}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            fullWidth
                            margin="normal"
                            disabled={isOperatorRole}
                        />
                    )}
                />
                {/* Admin-only fields */}
                {currentUser?.role === 'ADMIN' && record && (
                    <>
                        <Controller
                            control={control}
                            name="role"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value || "USER"}
                                    select
                                    fullWidth
                                    label={translate("users.fields.role")}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                    error={!!(errors as any)?.role}
                                    helperText={(errors as any)?.role?.message}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                    }}
                                >
                                    <MenuItem value="USER">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="USER" size="small" />
                                            <Typography variant="caption">{translate('user.create.roles.user')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="GUEST">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="GUEST" size="small" />
                                            <Typography variant="caption">{translate('user.create.roles.guest')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="SUB_MODERATOR">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="SUB_MODERATOR" size="small" color="info" />
                                            <Typography variant="caption">{translate('user.create.roles.subModerator')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="MODERATOR">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="MODERATOR" size="small" color="primary" />
                                            <Typography variant="caption">{translate('user.create.roles.moderator')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="MODERATOR_FREE">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="MODERATOR_FREE" size="small" color="primary" />
                                            <Typography variant="caption">{translate('user.create.roles.moderatorFree')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="SAFEE_MODERATOR">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="SAFEE_MODERATOR" size="small" color="warning" />
                                            <Typography variant="caption">{translate('user.create.roles.safeeModerator')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="SAFEE_STAFF">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="SAFEE_STAFF" size="small" color="warning" />
                                            <Typography variant="caption">{translate('user.create.roles.safeeStaff')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="VIGILANE">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="VIGILANE" size="small" color="error" />
                                            <Typography variant="caption">{translate('user.create.roles.vigilane')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="OT">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="OT" size="small" color="info" />
                                            <Typography variant="caption">{translate('user.create.roles.ot')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="OPERATOR">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="OPERATOR" size="small" color="info" />
                                            <Typography variant="caption">{translate('user.create.roles.operator')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                    <MenuItem value="ADMIN">
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Chip label="ADMIN" size="small" color="secondary" />
                                            <Typography variant="caption">{translate('user.create.roles.admin')}</Typography>
                                        </Stack>
                                    </MenuItem>
                                </TextField>
                            )}
                        />
                        <Controller
                            control={control}
                            name="subGroupId"
                            render={({ field }) => (
                                <Autocomplete
                                    options={subGroups}
                                    getOptionLabel={(option) => option.name}
                                    value={subGroups.find(sg => sg.id === field.value) || null}
                                    onChange={(_, newValue) => field.onChange(newValue?.id || null)}
                                    loading={loadingSubGroups}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={translate("users.fields.subGroup", "Sous-groupe")}
                                            margin="normal"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingSubGroups ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                            helperText={translate("users.fields.subGroupHelp", "Assigner l'utilisateur à un sous-groupe (optionnel)")}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box sx={{ pl: (option.depth || 0) * 2 }}>
                                                {option.name}
                                            </Box>
                                        </li>
                                    )}
                                />
                            )}
                        />
                        <TextField
                            {...register("password", {
                                minLength: {
                                    value: 8,
                                    message: translate("validation.minLength", { min: 8 }),
                                },
                            })}
                            error={!!(errors as any)?.password}
                            helperText={(errors as any)?.password?.message || translate("users.edit.passwordHelp")}
                            margin="normal"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            label={translate("users.edit.newPassword")}
                            name="password"
                        />
                        <TextField
                            {...register("confirmPassword", {
                                validate: (value) => {
                                    const password = watch("password");
                                    if (password && !value) {
                                        return translate("validation.confirmPassword");
                                    }
                                    if (value && value !== password) {
                                        return translate("validation.passwordMismatch");
                                    }
                                    return true;
                                },
                            })}
                            error={!!(errors as any)?.confirmPassword}
                            helperText={(errors as any)?.confirmPassword?.message}
                            margin="normal"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            type="password"
                            label={translate("users.edit.confirmPassword")}
                            name="confirmPassword"
                        />
                        <Controller
                            control={control}
                            name="hasBeenOnboarded"
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    label={translate("users.edit.hasBeenOnboarded")}
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={!!field.value}
                                            onChange={(event) => {
                                                field.onChange(event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="isPremium"
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    label="Premium"
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={!!field.value}
                                            onChange={(event) => {
                                                field.onChange(event.target.checked);
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                    </>
                )}
                {/* External ID field for OPERATOR/VIGILANE/OT/ADMIN/SAFEE_MODERATOR roles */}
                {canEditExternalId && (
                    <TextField
                        value={externalId}
                        onChange={(e) => setExternalId(e.target.value)}
                        margin="normal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        type="text"
                        label="External ID"
                        helperText={translate(
                            "users.edit.externalIdHelp",
                            "Identifiant de l'utilisateur dans votre logiciel métier"
                        )}
                    />
                )}
                {/* Sub-group selector for MODERATOR users (not ADMIN, since ADMIN already has it above) */}
                {['MODERATOR', 'MODERATOR_FREE', 'SAFEE_MODERATOR'].includes(currentUser?.role || '') && record && (
                    <Controller
                        control={control}
                        name="subGroupId"
                        render={({ field }) => (
                            <Autocomplete
                                options={subGroups}
                                getOptionLabel={(option) => option.name}
                                value={subGroups.find(sg => sg.id === field.value) || null}
                                onChange={(_, newValue) => field.onChange(newValue?.id || null)}
                                loading={loadingSubGroups}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={translate("users.fields.subGroup", "Sous-groupe")}
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingSubGroups ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                        helperText={translate("users.fields.subGroupHelp", "Assigner l'utilisateur à un sous-groupe (optionnel)")}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <Box sx={{ pl: (option.depth || 0) * 2 }}>
                                            {option.name}
                                        </Box>
                                    </li>
                                )}
                            />
                        )}
                    />
                )}
            </Box>
        </Edit>
    );
};
